// The numbers behind the period panels chart (PeriodPanels.js): net P&L, win rate and trade count
// for each month, week or trading day. Months and weeks with no trades are kept, so the time axis
// is even and a quiet stretch shows as a gap.
import { netPnl, tradingDayKey } from './pnl';
import { currentLocale, translateNow as tn } from './langState';

export const PERIOD_LIMIT = { month: 12, week: 16, day: 30 };

const keyDate = (key) => new Date(`${key}T12:00:00`);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Weeks start on Monday, like the trading week.
function weekKey(day) {
  const d = keyDate(day);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return iso(d);
}
const keyOf = (unit, day) => (unit === 'month' ? day.slice(0, 7) : unit === 'week' ? weekKey(day) : day);

function nextKey(unit, key) {
  if (unit === 'month') {
    const [y, m] = key.split('-').map(Number);
    return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
  }
  const d = keyDate(key);
  d.setDate(d.getDate() + 7);
  return iso(d);
}

export function periodLabel(unit, key, long = false) {
  const loc = currentLocale();
  if (unit === 'month') return keyDate(`${key}-15`).toLocaleDateString(loc, long ? { month: 'long', year: 'numeric' } : { month: 'short' });
  const date = keyDate(key).toLocaleDateString(loc, { month: 'short', day: 'numeric', ...(long ? { year: 'numeric' } : {}) });
  return long && unit === 'week' ? tn('Week of {date}', { date }) : date;
}

export function periodRows(trades, unit = 'month') {
  const by = new Map();
  for (const t of trades) {
    if (!t.date) continue;
    const key = keyOf(unit, tradingDayKey(t.date));
    if (!by.has(key)) by.set(key, []);
    by.get(key).push(t);
  }
  let keys = [...by.keys()].sort();
  if (!keys.length) return [];
  if (unit !== 'day') {
    const filled = [];
    for (let k = keys[0]; k <= keys[keys.length - 1]; k = nextKey(unit, k)) filled.push(k);
    keys = filled;
  }
  return keys.slice(-PERIOD_LIMIT[unit]).map((key) => {
    const list = by.get(key) || [];
    const net = list.reduce((s, t) => s + netPnl(t), 0);
    return {
      key, unit, label: periodLabel(unit, key), longLabel: periodLabel(unit, key, true),
      net, count: list.length, winRate: list.length ? (list.filter((t) => netPnl(t) > 0).length / list.length) * 100 : null,
    };
  });
}

// A smooth line through the points that never overshoots between them (monotone cubic), as an
// SVG path. Overshooting would draw a dip or peak that isn't in the data.
export function smoothPath(points) {
  if (points.length < 2) return points.length ? `M${points[0][0]},${points[0][1]}` : '';
  const n = points.length;
  const dx = [];
  const slope = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx.push(points[i + 1][0] - points[i][0]);
    slope.push((points[i + 1][1] - points[i][1]) / dx[i]);
  }
  const tangent = [slope[0]];
  for (let i = 1; i < n - 1; i += 1) tangent.push(slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2);
  tangent.push(slope[n - 2]);
  for (let i = 0; i < n - 1; i += 1) {
    if (slope[i] === 0) { tangent[i] = 0; tangent[i + 1] = 0; continue; }
    const a = tangent[i] / slope[i];
    const b = tangent[i + 1] / slope[i];
    const h = a * a + b * b;
    if (h > 9) { const s = 3 / Math.sqrt(h); tangent[i] = s * a * slope[i]; tangent[i + 1] = s * b * slope[i]; }
  }
  let d = `M${points[0][0]},${points[0][1]}`;
  for (let i = 0; i < n - 1; i += 1) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    d += ` C${x0 + dx[i] / 3},${y0 + (tangent[i] * dx[i]) / 3} ${x1 - dx[i] / 3},${y1 - (tangent[i + 1] * dx[i]) / 3} ${x1},${y1}`;
  }
  return d;
}
